/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { CluEntityDto } from 'src/clu/appShell/cluBuildSchemaPage/components/CluEntitiesList';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';

export type CluEntitiesCommandBarProps = TableCommandBarProps<CluEntityDto> & {
    onAdd: () => void;
    onEdit: () => void;
    onDelete: () => void;
};

const messages = defineMessages({
    add: {
        id: 'clu.cluEntitiesCommandBar.add',
        defaultMessage: 'Add'
    },
    edit: {
        id: 'clu.cluEntitiesCommandBar.edit',
        defaultMessage: 'Edit entity components'
    },
    delete: {
        id: 'clu.cluEntitiesCommandBar.delete',
        defaultMessage: 'Delete'
    },
    searchPlaceholder: {
        id: 'clu.cluEntitiesCommandBar.searchPlaceholder',
        defaultMessage: 'Search'
    }
});

export const CluEntitiesCommandBar = observer((props: CluEntitiesCommandBarProps) => {
    const { onAdd, onEdit, onDelete, selectedItems, query, onQueryChange } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const commandBarItems: ICommandBarItemProps[] = [
        {
            key: 'add',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="addCLUEntityButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={onAdd}
                            text={localize(messages.add)}
                            name={localize(messages.add)}
                            iconProps={{ iconName: 'Add' }}
                            disabled={!canWrite || item.disabled}
                            data-automation-id="addCLUEntityButton"
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'edit',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="editCLUEntityButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={onEdit}
                            text={localize(messages.edit)}
                            name={localize(messages.edit)}
                            iconProps={{ iconName: 'Edit' }}
                            disabled={!canWrite || item.disabled}
                            data-automation-id="editCLUEntityButton"
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="deleteCLUEntityButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={onDelete}
                            text={localize(messages.delete)}
                            name={localize(messages.delete)}
                            iconProps={{ iconName: 'Delete' }}
                            disabled={!canWrite || item.disabled}
                            data-automation-id="deleteCLUEntityButton"
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        }
    ];

    const items = commandBarItemSelectionManager(commandBarItems, ['add'], ['edit', 'delete'], ['delete'], selectedItems.length);

    return <LuisCommandBar items={items} query={query} includeSearchBar searchBarAlwaysExpanded onQueryChange={onQueryChange} />;
});
